
import React, { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { User, Shield, UserCog, UserPlus, Building2, ChevronDown, ChevronRight } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useUsersData } from './hooks/useUsersData';
import { useUserActions } from './hooks/useUserActions';
import { useAuth } from '@/contexts/AuthContext';
import UserActionsMenu from './components/UserActionsMenu';
import ConfirmationDialog from './components/ConfirmationDialog';
import SuspensionDialog from '@/components/company/components/SuspensionDialog';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';

export default function UsersManagement({ isSuperuser }: { isSuperuser: boolean }) {
  const { users, setUsers, loading, refetch } = useUsersData();
  const { handleStatusToggle, handleMakeAdmin, handleImpersonate, handleInviteSuperuser, handleUpdateUser } = useUserActions();
  const { profile } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const companyFilter = searchParams.get('company');
  const companyNameFilter = searchParams.get('companyName');

  const filteredUsers = companyFilter
    ? users.filter(u => u.company_name === decodeURIComponent(companyNameFilter || ''))
    : users;

  const groupedUsers = filteredUsers.reduce((groups, user) => {
    const company = user.company_name || 'No Company';
    if (!groups[company]) groups[company] = [];
    groups[company].push(user);
    return groups;
  }, {} as Record<string, typeof filteredUsers>);

  const sortedCompanies = Object.keys(groupedUsers).sort((a, b) => {
    if (a === 'No Company') return 1;
    if (b === 'No Company') return -1;
    return a.localeCompare(b);
  });

  const [collapsedCompanies, setCollapsedCompanies] = useState<Set<string>>(new Set());

  const toggleCompany = (company: string) => {
    setCollapsedCompanies(prev => {
      const next = new Set(prev);
      if (next.has(company)) next.delete(company);
      else next.add(company);
      return next;
    });
  };
  
  // State for confirmation dialogs
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: '',
    description: '',
    action: () => Promise.resolve(),
    actionLabel: ''
  });
  
  // State for user edit dialog
  const [editDialog, setEditDialog] = useState<{
    isOpen: boolean;
    userId: string;
    firstName: string;
    lastName: string;
    companyPosition: string;
    phoneNumber: string;
    email: string;
    role: string;
    companyName: string;
  }>({
    isOpen: false,
    userId: '',
    firstName: '',
    lastName: '',
    companyPosition: '',
    phoneNumber: '',
    email: '',
    role: '',
    companyName: ''
  });
  const [isSaving, setIsSaving] = useState(false);

  // State for invite superuser dialog
  const [inviteDialog, setInviteDialog] = useState({
    isOpen: false,
    email: ''
  });
  const [isInviting, setIsInviting] = useState(false);

  // State for suspension dialog (superuser suspending a user)
  const [suspensionDialog, setSuspensionDialog] = useState<{
    isOpen: boolean;
    userId: string;
    userName: string;
    companyId: string | null;
  }>({
    isOpen: false,
    userId: '',
    userName: '',
    companyId: null
  });

  const openSuspensionDialog = async (userId: string) => {
    const { data: profileData } = await supabase
      .from('profiles')
      .select('company_id, first_name, last_name')
      .eq('id', userId)
      .single();

    setSuspensionDialog({
      isOpen: true,
      userId,
      userName:
        [profileData?.first_name, profileData?.last_name]
          .filter(Boolean)
          .join(' ') || 'User',
      companyId: profileData?.company_id || null
    });
  };

  // Open confirmation dialog with proper context
  const openConfirmDialog = (title: string, description: string, action: () => Promise<void>, actionLabel: string) => {
    setConfirmDialog({
      isOpen: true,
      title,
      description,
      action,
      actionLabel
    });
  };

  // Function to open edit dialog
  const openEditDialog = (user: any) => {
    setEditDialog({
      isOpen: true,
      userId: user.id,
      firstName: user.profiles?.first_name || '',
      lastName: user.profiles?.last_name || '',
      companyPosition: user.company_position || '',
      phoneNumber: user.personal_phone_number || '',
      email: user.profiles?.email || '',
      role: user.role || '',
      companyName: user.company_name || 'No company'
    });
  };

  const handleEditSave = async () => {
    try {
      setIsSaving(true);
      await handleUpdateUser(
        editDialog.userId,
        {
          first_name: editDialog.firstName,
          last_name: editDialog.lastName,
          company_position: editDialog.companyPosition,
          personal_phone_number: editDialog.phoneNumber
        },
        setUsers
      );
      toast.success('User updated successfully');
      setEditDialog(prev => ({ ...prev, isOpen: false }));
    } catch (error: any) {
      toast.error(error.message || 'Failed to update user');
    } finally {
      setIsSaving(false);
    }
  };

  const handleInviteSubmit = async () => {
    if (!inviteDialog.email) return;
    try {
      setIsInviting(true);
      const inviterName =
        `${profile?.first_name || ''} ${profile?.last_name || ''}`.trim()
        || 'Platform Admin';
      await handleInviteSuperuser(inviteDialog.email, inviterName);
      toast.success('Superuser invitation sent');
      setInviteDialog({ isOpen: false, email: '' });
    } catch (error: any) {
      toast.error(error.message || 'Failed to send invitation');
    } finally {
      setIsInviting(false);
    }
  };

  const handleRemoveAdmin = async (userId: string) => {
    try {
      const { data: profileData } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('id', userId)
        .single();

      if (!profileData?.company_id) {
        throw new Error('Cannot find user company');
      }

      const { error } = await supabase
        .rpc('remove_admin_role' as any, {
          p_user_id: userId,
          p_company_id: profileData.company_id
        } as any);

      if (error) throw error;

      setUsers(prev => prev.map(u =>
        u.id === userId ? { ...u, role: 'user' } : u
      ));
      toast.success('Admin role removed');
    } catch (error: any) {
      toast.error(error?.message || 'Failed to remove admin role');
    }
  };

  const handleDeactivate = async (userId: string) => {
    openConfirmDialog(
      'Deactivate User',
      'Are you sure you want to deactivate this user? They will lose access and need to request reactivation.',
      async () => {
        const { data: profileData } = await supabase
          .from('profiles')
          .select('company_id')
          .eq('id', userId)
          .single();

        if (!profileData?.company_id) {
          throw new Error('Cannot find user company');
        }

        const { error } = await supabase
          .rpc('deactivate_user_from_company', {
            p_user_id: userId,
            p_company_id: profileData.company_id,
            p_reason: 'Deactivated by superuser'
          });

        if (error) throw error;

        setUsers(prev => prev.map(u =>
          u.id === userId
            ? { ...u, is_active: false, status: 'deactivated' }
            : u
        ));
      },
      'Deactivate'
    );
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'superuser':
        return <Shield className="h-4 w-4 text-amber-500" />;
      case 'admin':
        return <UserCog className="h-4 w-4 text-blue-500" />;
      default:
        return <User className="h-4 w-4 text-gray-500" />;
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Users Management</CardTitle>
          <CardDescription>Manage all system users</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex justify-center items-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Users Management</CardTitle>
          <CardDescription>Manage all system users</CardDescription>
        </div>
        {isSuperuser && (
          <Button
            onClick={() => setInviteDialog({ isOpen: true, email: '' })}
            size="sm"
          >
            <UserPlus className="h-4 w-4 mr-2" />
            Invite Superuser
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {companyFilter && (
          <div className="flex items-center gap-2 mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
            <Building2 className="h-4 w-4 text-blue-600" />
            <span className="text-sm text-blue-700">
              Showing users for: <strong>{decodeURIComponent(companyNameFilter || '')}</strong>
            </span>
            <Button
              variant="ghost"
              size="sm"
              className="ml-auto h-6 text-blue-600"
              onClick={() => navigate('/admin?tab=platform-users')}
            >
              Clear filter
            </Button>
          </div>
        )}
        {sortedCompanies.length > 0 ? (
          <Table className="table-fixed">
            <TableHeader>
              <TableRow>
                <TableHead className="w-[25%]">Name</TableHead>
                <TableHead className="w-[35%]">Email</TableHead>
                <TableHead className="w-[12%]">Role</TableHead>
                <TableHead className="w-[12%]">Status</TableHead>
                {isSuperuser && <TableHead className="w-[16%] text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedCompanies.map((company) => {
                const isCollapsed = collapsedCompanies.has(company);
                const companyUsers = groupedUsers[company];
                const colCount = isSuperuser ? 5 : 4;
                return (
                  <React.Fragment key={company}>
                    <TableRow
                      className="bg-muted/50 hover:bg-muted cursor-pointer"
                      onClick={() => toggleCompany(company)}
                    >
                      <TableCell colSpan={colCount} className="py-3">
                        <div className="flex items-center gap-3">
                          {isCollapsed ? (
                            <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                          ) : (
                            <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
                          )}
                          <Building2 className="h-4 w-4 text-muted-foreground shrink-0" />
                          <span className="font-semibold text-sm">{company}</span>
                          <span className="px-2 py-0.5 rounded-full text-xs bg-secondary text-secondary-foreground">
                            {companyUsers.length} {companyUsers.length === 1 ? 'user' : 'users'}
                          </span>
                        </div>
                      </TableCell>
                    </TableRow>
                    {!isCollapsed && companyUsers.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell className="w-[25%] font-medium">
                          {user.profiles ? `${user.profiles.first_name || ''} ${user.profiles.last_name || ''}`.trim() : 'Unknown'}
                        </TableCell>
                        <TableCell className="w-[35%]">{user.profiles?.email || 'No email'}</TableCell>
                        <TableCell className="w-[12%]">
                          <div className="flex items-center gap-2">
                            {getRoleIcon(user.role)}
                            {user.role}
                          </div>
                        </TableCell>
                        <TableCell className="w-[12%]">
                          <span className={`px-2 py-1 rounded-full text-xs ${
                            user.status === 'deactivated'
                              ? 'bg-gray-100 text-gray-800'
                              : user.status === 'suspended'
                              ? 'bg-amber-100 text-amber-800'
                              : 'bg-green-100 text-green-800'
                          }`}>
                            {user.status === 'deactivated'
                              ? 'Deactivated'
                              : user.status === 'suspended'
                              ? 'Suspended'
                              : 'Active'}
                          </span>
                        </TableCell>
                        {isSuperuser && (
                          <TableCell className="w-[16%] text-right">
                            <UserActionsMenu
                              user={user}
                              isSuperuser={isSuperuser}
                              onStatusToggle={() => {
                                if (user.is_active) {
                                  // Suspending — open SuspensionDialog
                                  openSuspensionDialog(user.id);
                                } else {
                                  // Activating — keep confirm flow
                                  openConfirmDialog(
                                    "Activate User",
                                    `Are you sure you want to activate ${user.profiles?.first_name || ''} ${user.profiles?.last_name || ''}?`,
                                    () => handleStatusToggle(user.id, user.is_active, setUsers),
                                    "Activate"
                                  );
                                }
                              }}
                              onMakeAdmin={() => openConfirmDialog(
                                "Make Admin",
                                `Are you sure you want to promote ${user.profiles?.first_name || ''} ${user.profiles?.last_name || ''} to admin?`,
                                () => handleMakeAdmin(user.id, setUsers),
                                "Make Admin"
                              )}
                              onRemoveAdmin={() => openConfirmDialog(
                                "Remove Admin",
                                `Are you sure you want to remove admin role from ${user.profiles?.first_name || ''} ${user.profiles?.last_name || ''}?`,
                                () => handleRemoveAdmin(user.id),
                                "Remove Admin"
                              )}
                              onImpersonate={() => handleImpersonate(user.id)}
                              onEdit={() => openEditDialog(user)}
                              onDeactivate={() => handleDeactivate(user.id)}
                            />
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        ) : (
          <div className="text-center py-6 text-muted-foreground">
            No users found
          </div>
        )}
      </CardContent>

      <ConfirmationDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        description={confirmDialog.description}
        actionLabel={confirmDialog.actionLabel}
        onClose={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmDialog.action}
      />

      <SuspensionDialog
        isOpen={suspensionDialog.isOpen}
        onClose={() => setSuspensionDialog(prev => ({ ...prev, isOpen: false }))}
        memberId={suspensionDialog.userId}
        memberName={suspensionDialog.userName}
        companyId={suspensionDialog.companyId}
        onSuspensionComplete={() => {
          setSuspensionDialog(prev => ({ ...prev, isOpen: false }));
          refetch();
        }}
      />

      <Dialog open={editDialog.isOpen} onOpenChange={(isOpen) => setEditDialog(prev => ({ ...prev, isOpen }))}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit {editDialog.firstName} {editDialog.lastName}</DialogTitle>
            <DialogDescription>
              {editDialog.email} · {editDialog.companyName} · {editDialog.role}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit-first-name">First name</Label>
                <Input
                  id="edit-first-name"
                  value={editDialog.firstName}
                  onChange={(e) => setEditDialog(prev => ({ ...prev, firstName: e.target.value }))}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="edit-last-name">Last name</Label>
                <Input
                  id="edit-last-name"
                  value={editDialog.lastName}
                  onChange={(e) => setEditDialog(prev => ({ ...prev, lastName: e.target.value }))}
                  className="mt-1"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="edit-position">Position</Label>
              <Input
                id="edit-position"
                value={editDialog.companyPosition}
                onChange={(e) => setEditDialog(prev => ({ ...prev, companyPosition: e.target.value }))}
                className="mt-1"
                placeholder="e.g. Project Manager"
              />
            </div>
            <div>
              <Label htmlFor="edit-phone">Phone number</Label>
              <Input
                id="edit-phone"
                value={editDialog.phoneNumber}
                onChange={(e) => setEditDialog(prev => ({ ...prev, phoneNumber: e.target.value }))}
                className="mt-1"
                placeholder="+27 82 123 4567"
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                value={editDialog.email}
                disabled
                className="mt-1 bg-muted"
              />
              <p className="text-xs text-muted-foreground mt-1">Email cannot be changed here</p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditDialog(prev => ({ ...prev, isOpen: false }))}
            >
              Cancel
            </Button>
            <Button
              onClick={handleEditSave}
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : 'Save changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={inviteDialog.isOpen}
        onOpenChange={(open) => !open && setInviteDialog({ isOpen: false, email: '' })}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invite Platform Superuser</DialogTitle>
            <DialogDescription>
              Send an invitation to give someone platform-level superuser access. They will receive an email with a link to create their account.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Label htmlFor="superuser-email">Email address</Label>
            <Input
              id="superuser-email"
              type="email"
              placeholder="admin@example.com"
              value={inviteDialog.email}
              onChange={(e) => setInviteDialog(prev => ({ ...prev, email: e.target.value }))}
              className="mt-2"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setInviteDialog({ isOpen: false, email: '' })}
            >
              Cancel
            </Button>
            <Button
              onClick={handleInviteSubmit}
              disabled={isInviting || !inviteDialog.email}
            >
              {isInviting ? 'Sending...' : 'Send Invitation'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
